import {
    ClipboardTextIcon,
    ExportIcon,
    LinkIcon,
    LinkBreakIcon,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { ActionBar } from '@/components';
import { ActionType } from '@/components/ActionBar';
import type { ActionConfig } from '@/components/ActionBar';
import { AddEventToReportPicker } from '@/components';
import { useEventsExport } from '@/hooks';
import { usePopoverCloseSignal } from '@/stores';
import { getEntityKindForClass } from '@/utils/detection-classes';
import { canAddEventToReport } from '@/utils/can-add-event-to-report';
import { exportableEvent } from '@/utils/export';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { getEventEntitiesCacheKey } from '@/constants/cache-keys';
import { EngineService } from '@/services/engine/engine-service';
import type { Event, EventEntities } from '@/types/event';
import type { FullSource } from '@/types/source';
import { useEventEntityLink } from '../hooks/useEventEntityLink';
import { LinkEntityPicker } from './LinkEntityPicker';

interface EventActionBarProps {
    event: Event;
    source: FullSource;
    query?: string | null;
    confidence?: number;
}

export function EventActionBar({
    event,
    source,
    query,
    confidence,
}: EventActionBarProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const closePopover = usePopoverCloseSignal((state) => state.trigger);
    const eventExport = useEventsExport();
    const exportable = exportableEvent(event, source);
    const canAddToReport = canAddEventToReport(event);

    const entityKind = canAddToReport
        ? getEntityKindForClass(event.classId)
        : null;

    const { data: entities } = useSWR<EventEntities>(
        entityKind ? getEventEntitiesCacheKey(event.id) : null,
        () => EngineService.getEventEntities(event.id),
    );

    const { link, unlink, isLinked } = useEventEntityLink({
        eventId: event.id,
        entities,
        entityKind,
    });

    const sourceEntityId =
        entityKind === 'person'
            ? (entities?.persons[0]?.person.id ?? '')
            : entityKind === 'vehicle'
              ? (entities?.vehicles[0]?.vehicle.id ?? '')
              : '';

    const actions: ActionConfig[] = [];

    if (entityKind) {
        if (isLinked) {
            actions.push({
                type: ActionType.ITEM,
                icon: LinkBreakIcon,
                label: t('eventView.actions.unlinkEntity'),
                onClick: () => void unlink(),
            });
        } else {
            actions.push({
                type: ActionType.ITEM,
                icon: LinkIcon,
                label: t('eventView.actions.linkEntity'),
                content: (
                    <LinkEntityPicker
                        entityKind={entityKind}
                        sourceEntityId={sourceEntityId}
                        onLink={(id) => void link(id)}
                        onClose={closePopover}
                    />
                ),
            });
        }
    }

    if (canAddToReport) {
        actions.push({
            type: ActionType.ITEM,
            icon: ClipboardTextIcon,
            label: t('actions.addToReport'),
            content: (
                <AddEventToReportPicker
                    event={event}
                    confidence={confidence}
                    query={query}
                    onClose={closePopover}
                />
            ),
        });
    }

    if (exportable) {
        actions.push({
            type: ActionType.ITEM,
            icon: ExportIcon,
            label: t('actions.export'),
            disabled: eventExport.busy,
            onClick: () => void eventExport.startExport([event.id]),
        });
    }

    if (actions.length === 0) {
        return null;
    }

    return <ActionBar actions={actions} />;
}
